import {
  CustomFieldProps,
  DynamicField,
  DynamicForm,
  FieldVariant,
  TextFieldProps,
  SwitchFieldProps,
  SelectOption,
  SelectFieldProps,
  EditorFieldProps,
  SearchableSelectOption,
  DateFieldProps,
} from '@/components/form-builder/form-builder.types';
import { StepperComponent } from '@/components/shared/stepper/stepper.component';
import { DacRepository } from '@/store/dac-state/dac-state.repository';
import { DacDocsFormResponse, DynaDocDetails, ParamGeneralFieldDto, User } from '@/types';
import { BehaviorSubject, combineLatest, map, Observable, of } from 'rxjs';
import { DacDocumentFormComponent } from '../../dac-document-form/dac-document-form.component';
import { DacValidateStepComponent } from '../../dac-validate-step/dac-validate-step.component';
import { DacPrixFormComponent } from '../../dac-prix-form/dac-prix-form.component';
import { ToggleSwitchChangeEvent } from 'primeng/toggleswitch';
import { DacShortlistFormComponent } from '../../dac-shortlist-form/dac-shortlist-form.component';
import { PreviewPdfComponent } from '@/components/shared/preview-pdf/preview-pdf.component';
import { DacResponsableInfosComponent } from '../../dac-responsable-infos/dac-responsable-infos.component';

interface getDacUpdateFormObjectProps {
  store: DacRepository;
  // multiFormAccordionForTransactionalForm: MultiFormAccordionService;
  dacId: number | null;
  dacVer: number | null;
  pdfUrl?: BehaviorSubject<string>;
  dynamicDocument: DynaDocDetails[];
  dynamicGeneralParam: Observable<ParamGeneralFieldDto[]>;

  validationValues: Record<number, DacDocsFormResponse[]>;
  activeIndex: Observable<number>;
  onActiveIndexChange?: (index: number) => void;
  preview?: (index: number) => void;
  currencyOptions?: SelectOption[];
  nonBlacklistedCompanies?: Observable<SearchableSelectOption[]>;
  memberOptions: Observable<User[]>;
  isUpdatable?: Observable<boolean>;
  dateRequired: Observable<boolean>;
}

export const getDacUpdateFormObject = ({
  store,

  // multiFormAccordionsForSubmissionPieces,

  dacId,
  dacVer,

  dynamicDocument,
  validationValues,
  activeIndex,
  onActiveIndexChange,
  preview,
  currencyOptions = [],
  nonBlacklistedCompanies,
  memberOptions,
  isUpdatable,
  pdfUrl,
  dateRequired,
  dynamicGeneralParam,
}: getDacUpdateFormObjectProps): DynamicForm => {
  const isDisabled = isUpdatable?.pipe(map(updatable => !updatable)) as Observable<boolean>;

  const aoField: DynamicField<TextFieldProps> = {
    label: 'dac.form.ao',
    variant: FieldVariant.TEXT,
    isRequired: true,
    isDisabled: of(true),
    props: {
      value: store.getNestedObservable<string>('dacInfo', 'aoReference'),
    },
  };

  const refField: DynamicField<TextFieldProps> = {
    label: 'dac.form.reference',
    variant: FieldVariant.TEXT,
    isRequired: true,
    isDisabled: of(true),
    props: {
      value: store.getNestedObservable<string>('dacInfo', 'reference'),
    },
  };

  const ppmField: DynamicField<TextFieldProps> = {
    label: 'dac.form.ppm',
    variant: FieldVariant.TEXT,
    isRequired: true,
    isDisabled: of(true),
    props: {
      value: store.getNestedObservable<string>('dacInfo', 'ppm', 'nom'),
    },
  };

  const milestoneField: DynamicField<TextFieldProps> = {
    label: 'dac.form.milestone',
    variant: FieldVariant.TEXT,
    isRequired: true,
    isDisabled: of(true),
    props: {
      value: store.getNestedObservable<string>('dacInfo', 'realisation', 'realisationEnvisagees'),
    },
  };

  const marketTypeField: DynamicField<TextFieldProps> = {
    label: 'dac.form.marketType',
    variant: FieldVariant.TEXT,
    isRequired: true,
    isDisabled: of(true),
    props: {
      value: store.getNestedObservable<string>('dacInfo', 'typeMarche', 'description'),
    },
  };

  const passingModeField: DynamicField<TextFieldProps> = {
    label: 'dac.form.passingMode',
    variant: FieldVariant.TEXT,
    isRequired: true,
    isDisabled: of(true),
    props: {
      value: store.getNestedObservable<Date>('dacInfo', 'modePassation', 'libelle'),
    },
  };

  const responsableField: DynamicField<SelectFieldProps> = {
    label: 'dac.form.responsable',
    variant: FieldVariant.SELECT,
    isRequired: true,
    isDisabled,
    error: store.getNestedObservable<string>('errors', 'responsableDacId'),
    props: {
      filterEnabled: true,
      options: memberOptions?.pipe(
        map(users => users.map(user => ({ name: `${user.firstName} ${user.lastName} - ${user.indication}`, code: user.id })))
      ),
      value: store.getNestedObservable<SelectOption>('responsableSelected'),
      onSelectChange: (value: SelectOption) => {
        store.setNested('responsableSelected', value);
        store.setNested('dacUpdateDto', 'responsableDacId', value?.code);
        store.clearError('responsableDacId');
      },
      placeholder: 'dac.form.placeholders.responsable',
    },
  };

  const responsableInformationCustomField: DynamicField<CustomFieldProps> = {
    label: '',
    variant: FieldVariant.CUSTOM,
    isDisabled: isUpdatable,
    isHidden: store.getObservable<SelectOption>('responsableSelected').pipe(map(responsable => !responsable.code)),
    props: {
      component: DacResponsableInfosComponent,
      injectKeys: ['responsableData'],
      responsableData: combineLatest([store.getNestedObservable<SelectOption>('responsableSelected'), memberOptions]).pipe(
        map(([selectoption, members]) => {
          if (!selectoption) return undefined;
          if (!members) return undefined;
          const member = members.find(member => member.id === selectoption?.code);
          return {
            firstName: member?.firstName,
            lastName: member?.lastName,
            email: member?.email,
            tel: member?.tel,
            adresse: member?.adresse,
            indication: member?.indication,
          };
        })
      ),
    },
  };

  const isDateFieldsDisabled = dateRequired.pipe(map(required => !required));

  const dateLimiteSoumissionField: DynamicField<DateFieldProps> = {
    label: 'dac.form.submissionDeadline',
    variant: FieldVariant.DATE,
    isRequired: false,
    isDisabled: isDateFieldsDisabled,
    error: store.getNestedObservable<string>('errors', 'dateLimiteSoumission'),
    props: {
      value: store.getNestedObservable<Date>('dacUpdateDto', 'dateLimiteSoumission'),
      onChange: (value: Date) => {
        store.setNested('dacUpdateDto', 'dateLimiteSoumission', value);
        store.clearError('dateLimiteSoumission');
      },
      placeholder: 'dac.form.placeholders.submissionDeadline',
      showTime: true,
      view: 'date',
    },
  };

  const dateLimiteRecptionDemandeField: DynamicField<DateFieldProps> = {
    label: 'dac.form.requestDeadline',
    variant: FieldVariant.DATE,
    isRequired: false,
    isDisabled: isDateFieldsDisabled,
    error: store.getNestedObservable<string>('errors', 'dateLimiteRecptionDemande'),
    props: {
      value: store.getNestedObservable<Date>('dacUpdateDto', 'dateLimiteRecptionDemande'),
      onChange: (value: Date) => {
        store.setNested('dacUpdateDto', 'dateLimiteRecptionDemande', value);
        store.clearError('dateLimiteRecptionDemande');
      },
      placeholder: 'dac.form.placeholders.requestDeadline',
      showTime: true,
      view: 'date',
    },
  };

  const dateOuvertureField: DynamicField<DateFieldProps> = {
    label: "Date d'ouverture",
    variant: FieldVariant.DATE,
    isRequired: false,
    isDisabled: isDateFieldsDisabled,
    error: store.getNestedObservable<string>('errors', 'dateOuverture'),
    props: {
      value: store.getNestedObservable<Date>('dacUpdateDto', 'dateOuverture'),
      onChange: (value: Date) => {
        store.setNested('dacUpdateDto', 'dateOuverture', value);
        store.clearError('dateOuverture');
      },
      placeholder: "Sélectionnez la date d'ouverture",
      showTime: true,
      view: 'date',
    },
  };

  const dateDebutDepotField: DynamicField<DateFieldProps> = {
    label: 'Date de début de dépôt',
    variant: FieldVariant.DATE,
    isRequired: false,
    isDisabled: isDateFieldsDisabled,
    error: store.getNestedObservable<string>('errors', 'dateDebutDepot'),
    props: {
      value: store.getNestedObservable<Date>('dacUpdateDto', 'dateDebutDepot'),
      onChange: (value: Date) => {
        store.setNested('dacUpdateDto', 'dateDebutDepot', value);
        store.clearError('dateDebutDepot');
      },
      placeholder: 'Sélectionnez la date de début de dépôt',
      showTime: false,
      view: 'date',
    },
  };

  const conditionField: DynamicField<EditorFieldProps> = {
    label: 'dac.form.condition',
    variant: FieldVariant.EDITOR,
    isRequired: false,
    isDisabled,
    props: {
      value: store.getNestedObservable<string>('dacUpdateDto', 'conditionsParticipation'),
      onChange: (value: string) => store.setNested('dacUpdateDto', 'conditionsParticipation', value),
      height: '320px',
    },
  };

  // The standalone "Paramètres généraux" step is retired: global/shared variables are no
  // longer collected in a separate step disconnected from their section. They render inline
  // inside each document's own form below (see DacDocumentFormComponent), in that document's
  // field order, and are pre-filled/disabled automatically wherever they've already been
  // answered on an earlier document in this DAC (same value, shared across the whole DAC).
  const steps = [
    ...dynamicDocument.map((doc, index) => ({
      value: index + 1,
      label: doc.name,
      component: DacDocumentFormComponent,
      injectKeys: ['dacId', 'document', 'validationValues'],
      dacId,
      document: doc,
      validationValues: validationValues[doc.id],
    })),
    {
      value: dynamicDocument.length + 1,
      label: 'Résumé de la soumission',
      component: DacValidateStepComponent,
      injectKeys: ['documents'],
      documents: dynamicDocument,
    },
  ];

  const stepperField: DynamicField<CustomFieldProps> = {
    label: '',
    variant: FieldVariant.CUSTOM,
    isHidden: isDisabled,
    props: {
      component: StepperComponent,
      injectKeys: ['isDisabled', 'stepperBuilder', 'activeIndex'],
      activeIndex: activeIndex,
      stepperBuilder: {
        orientation: 'vertical',
        onActiveIndexChange: onActiveIndexChange,
        items: steps,
        // Document steps now start at value 1 (the old, non-document "Paramètres généraux"
        // step at value 1 is gone), so every step except the final "Résumé" step has a
        // document to preview - and `preview(index)` maps directly to `documents[index - 1]`.
        footerActions: activeIndex.pipe(
          map(index =>
            index !== steps.length
              ? [
                  {
                    label: 'Prévisualiser le document',
                    severity: 'info',
                    onClick: () => preview?.(index),
                  },
                ]
              : []
          )
        ),
      },
    },
  };

  const payantField: DynamicField<SwitchFieldProps> = {
    label: 'dac.form.payed',
    variant: FieldVariant.SWITCH,
    isRequired: false,
    isDisabled,
    props: {
      checked: store.getNestedObservable<boolean>('dacUpdateDto', 'payant'),
      onChange: (event: ToggleSwitchChangeEvent) => {
        store.setNested('dacUpdateDto', 'payant', event.checked, false);
        if (!event.checked) store.setNested('dacUpdateDto.dacPrixRequests', []);
      },
    },
  };

  const dacPrixField: DynamicField<CustomFieldProps> = {
    label: 'dac.form.prices',
    variant: FieldVariant.CUSTOM,
    isHidden: store.getNestedObservable<boolean>('dacUpdateDto', 'payant').pipe(map(payant => !payant)),
    props: {
      component: DacPrixFormComponent,
      injectKeys: ['value', 'onChange', 'currencyOptions', 'showField', 'hiddenHeader', 'disbaled'],
      value: store.getNestedObservable('dacUpdateDto.dacPrixRequests'),
      onChange: (value: any) => store.setNested('dacUpdateDto.dacPrixRequests', value),
      currencyOptions: currencyOptions,
      disabled: isDisabled,
    },
  };

  const shortListingMembersField: DynamicField<CustomFieldProps> = {
    label: 'dac.form.shortListing',
    variant: FieldVariant.CUSTOM,
    isRequired: false,
    isHidden: store.getNestedObservable<boolean>('dacInfo', 'shortListing').pipe(map(shortListing => !shortListing)),
    props: {
      component: DacShortlistFormComponent,
      injectKeys: ['value', 'onChange', 'options', 'disabled', 'errors', 'globalError', 'hiddenHeader', 'displayAsList'],
      displayAsList: true,
      options: nonBlacklistedCompanies,
      value: combineLatest([
        store.getObservable('dacInfo'),
        store.getNestedObservable<number[]>('dacUpdateDto', 'shortlistMembers')
      ]).pipe(
        map(([dacInfo, dtoList]: [any, number[]]) => {
          if (dacInfo?.etapeDac !== '1') {
            return dacInfo?.dacShortlist?.map((company: any) => Array.isArray(company) ? company.map((c: any) => c.id) : company.id) || [];
          }
          return dtoList;
        })
      ),
      onChange: (value: number[]) => {
        store.setNested('dacUpdateDto', 'shortlistMembers', value);
      },
      disabled: combineLatest([isDisabled, store.getNestedObservable<string>('dacInfo', 'etapeDac')]).pipe(map(([dis, etape]) => dis || etape === '2')),
    },
  };

  const previewField: DynamicField<CustomFieldProps> = {
    label: 'dac.form.preview',
    variant: FieldVariant.CUSTOM,
    isHidden: isUpdatable,
    props: {
      component: PreviewPdfComponent,
      injectKeys: ['src'],
      src: pdfUrl,
    },
  };

  // const transactionalFormField: DynamicField<CustomFieldProps> = {
  //   label: 'dac.form.transactionalForms',
  //   variant: FieldVariant.CUSTOM,
  //   props: {
  //     component: MultiFormAccordionComponent,
  //     injectKeys: ['accordionObject'],
  //     accordionObject: {
  //       titles: ['dac.form.transactionalForms2'],
  //       component: DacTransactionalFormComponent,
  //       injectKeys: ['dacId', 'dacVer', 'isDisabled'],
  //       dacId,
  //       dacVer,
  //       isDisabled,
  //       allowEmpty: of(true),
  //       customFactoryMultiFormAccordion: multiFormAccordionForTransactionalForm,
  //     },
  //   },
  // };

  return {
    title: 'Document dynamique',
    description: '',
    isHeaderHidden: true,
    grids: [
      {
        title: 'dac.form.tabs.overview',
        gridItems: [
          {
            fields: [aoField, refField],
          },
          {
            fields: [ppmField, milestoneField],
          },
          {
            fields: [marketTypeField, passingModeField],
          },
          {
            fields: [dateLimiteSoumissionField, dateLimiteRecptionDemandeField],
          },
          {
            fields: [dateDebutDepotField, dateOuvertureField],
          },
          {
            fields: [responsableField],
          },
          { fields: [responsableInformationCustomField] },
          { fields: [shortListingMembersField] },
          { fields: [payantField] },
          { fields: [dacPrixField] },
          { fields: [conditionField] },
        ],
      },
      {
        title: 'dac.form.tabs.dynamic',
        gridItems: [
          {
            fields: [stepperField],
          },
          { fields: [previewField] },
        ],
      },
    ],
  };
};
